import { useEffect, useState, useMemo } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import Pagination from "../../../../components/Pagination";
import api from "../../../../api/api";
import * as bootstrap from "bootstrap";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Table from "../../../../components/table";
import usePagination from "../../../../utils/usePagination";
import "../../Common/form.css";
  
const ChargeCode = () => {
  const [chargeCodes, setChargeCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
 
  const [selectedChargeCode, setSelectedChargeCode] = useState(null);
  const [editForm, setEditForm] = useState({
    sac_code: "",
    tax_percentage: "",
    currencies: ""
  });
 
  const filteredData = useMemo(() => {
    return chargeCodes.filter(
      (code) =>
        code.charge_code?.toLowerCase().includes(search.toLowerCase()) ||
        code.sac_code?.toLowerCase().includes(search.toLowerCase()) ||
        code.currencies?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [chargeCodes, search]);
 
  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredData, 10);
 
  const fetchChargeCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/charge_code/all");
      setChargeCodes(response.data);
    } catch (err) {
      setError("Failed to fetch charge codes");
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => {
    fetchChargeCodes();
  }, []);
 
  useEffect(() => {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );
    const tooltips = tooltipTriggerList.map((el) => new bootstrap.Tooltip(el));
    return () => tooltips.forEach((t) => t.dispose());
  }, [currentItems]);
 
  const handleEditClick = async (item) => {
    try {
      setSelectedChargeCode(item);
      setEditForm({
        sac_code: item.sac_code || "",
        tax_percentage: item.tax_percentage || "",
        currencies: item.currencies || ""
      });
 
      const modalEl = document.getElementById("editModal");
      const modal = new bootstrap.Modal(modalEl);
      modal.show();
    } catch (err) {
      alert("Could not load details.");
    }
  };
 
  const handleSubmit = async () => {
    if (!selectedChargeCode) return;
    try {
      await api.put(
        `/charge_code/${selectedChargeCode.charge_code_id}`,
        editForm,
      );
 
      setChargeCodes((prev) =>
        prev.map((row) =>
          row.charge_code_id === selectedChargeCode.charge_code_id
            ? { ...row, ...editForm }
            : row,
        ),
      );
 
      bootstrap.Modal.getInstance(document.getElementById("editModal")).hide();
    } catch (err) {
      console.error("Update failed", err);
      alert("Failed to update charge code");
    }
  };
 
  const columns = [
    { label: "Charge Code", accessor: "charge_code" },
    { label: "SAC Code", accessor: "sac_code" },
    { label: "GST %", accessor: "tax_percentage" },
    { label: "Default currency", accessor: "currencies" },
    {
      label: "Edit",
      render: (row) => (
        <EditIcon
          onClick={() => handleEditClick(row)}
          style={{ cursor: "pointer", color: "#1e305e" }}
        />
      ),
    },
  ];
 
  if (error) return <div className="alert alert-danger m-3">{error}</div>;
 
  return (
    <>
      <Breadcrumbs />
      <div className="mb-3 d-flex align-items-center position-relative">
        <input
          type="text"
          className="form-control"
          placeholder="Search by charge code, sac code or currencies..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            goToPage(1);
          }}
        />
        {search && (
          <CancelIcon
            className="position-absolute"
            onClick={() => setSearch("")}
            style={{ right: "15px", cursor: "pointer", color: "#6c757d" }}
          />
        )}
      </div>
      <Table
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="No charge codes found."
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />
 
      <div
        className="modal fade"
        id="editModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Charge Code</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <div className="modal-body">
              {selectedChargeCode && (
                <>
                  <p>
                    <strong>Charge Code:</strong> {selectedChargeCode.charge_code}
                  </p>
                  <div className="mt-3">
                    <label className="form-label">SAC Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.sac_code}
                      onChange={(e) => setEditForm({ ...editForm, sac_code: e.target.value })}
                    />
                  </div>
                  <div className="mt-3">
                    <label className="form-label">GST %</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editForm.tax_percentage}
                      onChange={(e) => setEditForm({ ...editForm, tax_percentage: e.target.value })}
                    />
                  </div>
                  <div className="mt-3">
                    <label className="form-label">Currencies</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., INR, USD, EUR"
                      value={editForm.currencies}
                      onChange={(e) => setEditForm({ ...editForm, currencies: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" data-bs-dismiss="modal">
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSubmit}>
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
 
export default ChargeCode;
